import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { I18nPipe } from '@delon/theme';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

@Component({
  standalone: true,
  selector: 'log-table',
  template: `
    <nz-alert
      *ngIf="showLimitAlert && (data.length >= 10 || indentLevel >= 4)"
      nzType="info"
      [nzMessage]="'log.data-display-limit' | i18n"
      [nzDescription]="limitDescription"
      nzShowIcon
      class="limit-alert"
      [testId]="'log-limit-alert'"
    />
    <ng-template #limitDescription>
      <div>
        <p *ngIf="data.length >= 10">{{ 'log.data-limit-per-level' | i18n }}</p>
        <p *ngIf="indentLevel >= 4">{{ 'log.level-limit' | i18n }}</p>
      </div>
    </ng-template>
    <nz-table
      #logTable
      [nzData]="limitedData"
      [nzShowPagination]="showPagination"
      [nzSize]="size"
      [nzFrontPagination]="false"
      [nzLoading]="loading"
      [nzTotal]="total"
      [nzPageSize]="pageSize"
      [nzPageIndex]="pageIndex"
      (nzPageIndexChange)="pageIndexChange.emit($event)"
      (nzPageSizeChange)="pageSizeChange.emit($event)"
      [nzShowSizeChanger]="showSizeChanger"
      nzTableLayout="fixed"
      [class.nested-table]="indentLevel > 0"
      [ngClass]="'indent-level-' + indentLevel"
      style="width: 100%"
      [testId]="'log-table'"
    >
      <thead *ngIf="!hideHeader; else hideHeaderTemplate" [testId]="'log-table-header'">
        <tr [testId]="'log-table-header-row'">
          <th scope="col" *ngIf="expandable" [nzWidth]="'5%'" [testId]="'log-table-expand-header'"></th>
          <th scope="col" [nzWidth]="'15%'" [testId]="'log-table-description-header'">{{ 'log.description' | i18n }}</th>
          <th scope="col" [nzWidth]="'25%'" [nzEllipsis]="true" [testId]="'log-table-request-path-header'">{{
            'log.request-path' | i18n
          }}</th>
          <th scope="col" [nzWidth]="'25%'" [nzEllipsis]="true" [testId]="'log-table-request-time-header'">{{
            'log.request-time' | i18n
          }}</th>
          <th scope="col" [nzWidth]="'10%'" [nzEllipsis]="true" [testId]="'log-table-time-cost-header'">{{ 'log.time-cost' | i18n }}</th>
          <th scope="col" [nzWidth]="'10%'" [testId]="'log-table-status-header'">{{ 'log.status' | i18n }}</th>
          <th scope="col" [nzWidth]="'10%'" [testId]="'log-table-actions-header'">{{ 'log.actions' | i18n }}</th>
        </tr>
      </thead>
      <ng-template #hideHeaderTemplate>
        <thead style="visibility: hidden; height: 0; overflow: hidden; position: absolute;">
          <tr style="height: 0; border: none;">
            <th scope="col" *ngIf="expandable" [nzWidth]="'5%'" style="padding: 0; border: none;"></th>
            <th scope="col" [nzWidth]="'15%'" style="padding: 0; border: none;"></th>
            <th scope="col" [nzWidth]="'25%'" style="padding: 0; border: none;"></th>
            <th scope="col" [nzWidth]="'25%'" style="padding: 0; border: none;"></th>
            <th scope="col" [nzWidth]="'10%'" style="padding: 0; border: none;"></th>
            <th scope="col" [nzWidth]="'10%'" style="padding: 0; border: none;"></th>
            <th scope="col" [nzWidth]="'10%'" style="padding: 0; border: none;"></th>
          </tr>
        </thead>
      </ng-template>
      <tbody [testId]="'log-table-body'">
        @for (item of logTable.data; track item) {
          <tr [testId]="'log-table-row'">
            <td
              *ngIf="expandable"
              [(nzExpand)]="item.expand"
              (nzExpandChange)="onExpandChange(item)"
              [ngClass]="'expand-icon-level-' + indentLevel"
              [testId]="'log-table-expand-cell'"
            ></td>
            <td [testId]="'log-table-description-cell'">
              <span class="description-cell" [class.indented]="indentLevel > 0" [testId]="'log-table-description-content'">
                <span *ngIf="indentLevel > 0" class="tree-line" [style.left.px]="-16" [testId]="'log-table-tree-line'"></span>
                {{ item.description }}
              </span>
            </td>
            <td [testId]="'log-table-request-path-cell'">
              <span class="ellipsis-text" [title]="item.requestPath" [testId]="'log-table-request-path-content'">{{
                item.requestPath
              }}</span>
            </td>
            <td [testId]="'log-table-request-time-cell'">{{ item.requestTime | date: 'yyyy-MM-dd HH:mm:ss.SSS' }}</td>
            <td [testId]="'log-table-time-cost-cell'">{{ item.timeCost }} ms</td>
            <td [testId]="'log-table-status-cell'">
              <nz-tag [nzColor]="item.hasError === true ? 'error' : 'success'" class="status-tag" [testId]="'log-table-status-tag'">
                {{ item.hasError === true ? ('log.failed' | i18n) : ('log.success' | i18n) }}
              </nz-tag>
            </td>
            <td [testId]="'log-table-actions-cell'">
              <a class="detail-link" (click)="viewDetail.emit(item)" [testId]="'log-table-detail-link'">{{ 'log.view-detail' | i18n }}</a>
            </td>
          </tr>
          <tr [nzExpand]="item.expand" *ngIf="item.children?.length && indentLevel < 4" [testId]="'log-table-nested-row'">
            <td [attr.colspan]="expandable ? 7 : 6" [testId]="'log-table-nested-cell'">
              <div class="inner-table-container" [testId]="'log-table-inner-container'">
                <log-table
                  [data]="item.children"
                  [showPagination]="false"
                  [size]="'middle'"
                  [expandable]="true"
                  [hideHeader]="true"
                  [indentLevel]="indentLevel + 1"
                  [showLimitAlert]="true"
                  (expandChange)="onExpandChange($event)"
                  (viewDetail)="viewDetail.emit($event)"
                  [testId]="'log-table-nested'"
                />
              </div>
            </td>
          </tr>
          <tr [nzExpand]="item.expand" *ngIf="item.children?.length && indentLevel >= 4" [testId]="'log-table-max-level-row'">
            <td [attr.colspan]="expandable ? 7 : 6" [testId]="'log-table-max-level-cell'">
              <div class="max-level-reached" [testId]="'log-table-max-level-message'">
                <p [testId]="'log-table-max-level-text'">{{ 'log.max-level-reached' | i18n }}</p>
              </div>
            </td>
          </tr>
        }
      </tbody>
    </nz-table>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .description-cell {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 32px;
        padding: 4px 0;
      }

      .tree-line {
        position: absolute;
        width: 12px;
        height: 32px;
        border-left: 1px solid #d9d9d9;
        border-bottom: 1px solid #d9d9d9;
      }

      .ellipsis-text {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .status-tag {
        min-width: 48px;
        text-align: center;
      }

      .detail-link {
        color: #1890ff;
        cursor: pointer;
      }

      .detail-link:hover {
        color: #40a9ff;
      }

      .inner-table-container {
        padding: 12px 16px;
        margin-left: 24px;
        background-color: transparent;
        border-top: none;
        border-left: 3px solid #1890ff;
      }

      .limit-alert {
        margin-bottom: 16px;
      }

      .max-level-reached {
        padding: 16px;
        text-align: center;
        color: #999;
        background-color: #f9f9f9;
        border-radius: 4px;
      }

      :host ::ng-deep {
        .ant-table {
          background: transparent;

          .ant-table-thead > tr > th {
            background: #fafafa;
            border-bottom: 1px solid #f0f0f0;
            padding: 16px 12px;
            &:first-child {
              padding-left: 12px;
            }
          }

          .ant-table-tbody {
            > tr > td {
              padding: 16px 12px;
              border-bottom: 1px solid #f5f5f5;
              &:first-child {
                padding-left: 12px;
              }
            }

            > tr.ant-table-expanded-row {
              > td {
                padding: 8px 0 !important;
                background: transparent !important;
                border-bottom: none !important;
              }
            }

            > tr:first-child > td {
              border-top: none;
            }

            > tr:last-child > td {
              border-bottom: none;
            }
          }

          &.ant-table-middle {
            .ant-table-tbody > tr > td {
              padding: 12px;
            }
            margin: 0 !important;
          }
        }

        .ant-table-row-expand-icon-cell {
          width: 48px !important;
          padding-right: 8px !important;
        }

        .ant-table-wrapper {
          border-radius: 4px;
          margin-bottom: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        /* 确保嵌套表格列宽与父表格一致 */
        .nested-table {
          .ant-table-thead > tr > th,
          .ant-table-tbody > tr > td {
            &:nth-child(1) {
              width: 5% !important;
            }
            &:nth-child(2) {
              width: 15% !important;
            }
            &:nth-child(3) {
              width: 25% !important;
            }
            &:nth-child(4) {
              width: 25% !important;
            }
            &:nth-child(5) {
              width: 10% !important;
            }
            &:nth-child(6) {
              width: 10% !important;
            }
            &:nth-child(7) {
              width: 10% !important;
            }
          }
        }
      }

      /* 嵌套层级样式 */
      :host ::ng-deep {
        .nested-table {
          margin-left: 0;

          .ant-table {
            background-color: #ffffff;
          }
        }

        .expand-icon-level-0 {
          .ant-table-row-expand-icon {
            border-color: #1890ff !important;
            color: #1890ff !important;
          }
        }

        .expand-icon-level-1 {
          .ant-table-row-expand-icon {
            border-color: #52c41a !important;
            color: #52c41a !important;
          }
        }

        .expand-icon-level-2 {
          .ant-table-row-expand-icon {
            border-color: #faad14 !important;
            color: #faad14 !important;
          }
        }

        .expand-icon-level-3 {
          .ant-table-row-expand-icon {
            border-color: #eb5942 !important;
            color: #eb5942 !important;
          }
        }
        .expand-icon-level-4 {
          .ant-table-row-expand-icon {
            border-color: #f5222d !important;
            color: #f5222d !important;
          }
        }

        .indent-level-1 {
          .inner-table-container {
            border-left-color: #52c41a;
          }
        }

        .indent-level-2 {
          .inner-table-container {
            border-left-color: #faad14;
          }
        }

        .indent-level-3 {
          .inner-table-container {
            border-left-color: #eb5942;
          }
        }

        .indent-level-4 {
          .inner-table-container {
            border-left-color: #f5222d;
          }
        }
      }
    `
  ],
  imports: [NzTableModule, NzTagModule, CommonModule, NzAlertModule, I18nPipe, TestIdDirective]
})
export class LogTableComponent {
  @Input() data: any[] = [];
  @Input() showPagination = true;
  @Input() size: 'default' | 'middle' | 'small' = 'default';
  @Input() loading = false;
  @Input() total = 0;
  @Input() pageSize = 10;
  @Input() pageIndex = 1;
  @Input() showSizeChanger = true;
  @Input() expandable = false;
  @Input() hideHeader = false;
  @Input() indentLevel = 0;
  @Input() showLimitAlert = false;

  @Output() readonly expandChange = new EventEmitter<any>();
  @Output() readonly pageIndexChange = new EventEmitter<number>();
  @Output() readonly pageSizeChange = new EventEmitter<number>();
  @Output() readonly viewDetail = new EventEmitter<any>();

  get limitedData(): any[] {
    if (!this.data) {
      return [];
    }
    return this.data.length > 10 ? this.data.slice(0, 10) : this.data;
  }

  onExpandChange(item: any) {
    this.expandChange.emit(item);
  }
}
